import {
  Box,
  Button,
  FormControl,
  Grid,
  Select,
  Stack,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react'
import type { IInsuranceParams } from 'BrokerTsLib/src/dtos'
import { Insurer, Product } from 'BrokerTsLib/src/enums'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { usePartner } from '@/context/PartnerContext'
import { Apps } from '@/dtos'
import { useApp } from '@/hooks/useApp'
import {
  useCloneInsuranceParams,
  useFindContractNumber,
} from '@/hooks/useInsuranceParams'
import { Cloud } from '@/icons'
import { GetAppPath } from '@/utils/appUtils'

import type { ConfirmModalProps } from './ConfirmModal'
import { ConfirmModal } from './ConfirmModal'
import { FormLabel } from './FormLabel'
import { InputGroup } from './InputGroup'

export const ContractReplacementModal = (modalProps: Omit<ConfirmModalProps, 'children'>) => {
  const navigate = useNavigate()
  const { product, app } = useApp()
  const tooltipText = useMemo(() => getTooltipText(product!), [product])

  const [foundParamsId, setFoundParamsId] = useState<number | undefined | null>(
    undefined,
  )

  const { mutateAsync: cloneInsuranceParams, isPending: isLoadingClone } =
    useCloneInsuranceParams()
  const cloneReturnUrl = `/${GetAppPath(app)}steps/1`

  return (
    <ConfirmModal
      title="Náhrada smlouvy"
      {...modalProps}
      yesButton={
        foundParamsId === undefined ? undefined : (
          <Button
            colorScheme="green"
            isDisabled={isLoadingClone}
            isLoading={isLoadingClone}
            onClick={() => {
              if (foundParamsId === null) {
                navigate(`${cloneReturnUrl}`)
                modalProps.onClose(true)
              } else {
                cloneInsuranceParams({
                  paramsId: foundParamsId!.toString(),
                  replacement: true,
                })
                  .then(({ data: { insuranceParamsId } }) => {
                    navigate(`${cloneReturnUrl}/${insuranceParamsId}`)
                    modalProps.onClose(true)
                  })
                  .catch((data) => {
                    alert('Při klonování došlo k chybě')
                  })
              }
            }}
          >
            {foundParamsId === null ? 'Rozumím' : 'Ano, předvyplnit'}
          </Button>
        )
      }
      noButtonContent={
        foundParamsId ? 'Pokračovat bez předvyplnění' : undefined
      }
    >
      <Stack gap={8}>
        <Text fontSize={'sm'}>{tooltipText}</Text>

        <ContractReplacement
          modalVersion
          defaultReplacement
          onFound={(foundParamsId) => {
            setFoundParamsId(foundParamsId)
          }}
          foundContent={
            <Text mt={6} fontWeight="bold">
              Smlouva byla nalezena. Přejete si vyplnit data podle původní
              smlouvy?
            </Text>
          }
          notFoundContent={
            <Text mt={6} fontWeight="bold">
              Smlouva nebyla nalezena a data není možné z původní smlouvy
              načíst. Zadejte prosím údaje ručně.
            </Text>
          }
        />
      </Stack>
    </ConfirmModal>
  )
}

type ContractReplacementProps = {
  modalVersion?: boolean
  defaultReplacement?: boolean
  foundContent?: React.ReactNode
  notFoundContent?: React.ReactNode
  onFound?: (foundParamsId?: number | null) => void
  isDisabled?: boolean
}

const vehicleTooltipText =
  'Pouze pro pojišťovny Allianz, ČPP a Kooperativa. Náhradou je sjednání nové pojistné smlouvy, která nahrazuje a ruší pojistnou smlouvu předešlou. Konec pojištění nahrazované smlouvy je shodný s počátkem pojištění navazující smlouvy.'
const propertyTooltipText =
  'Pouze pro pojišťovny Allianz, ČPP a ČSOB. Náhradou je sjednání nové pojistné smlouvy, která nahrazuje a ruší pojistnou smlouvu předešlou. Konec pojištění nahrazované smlouvy je shodný s počátkem pojištění navazující smlouvy.'

const getTooltipText = (product: Product) => {
  switch (product) {
    case Product.LiabilityInsurance:
    case Product.PropertyInsurance:
      return propertyTooltipText
    case Product.VehicleInsurance:
      return vehicleTooltipText
    default:
      return ''
  }
}

export const ContractReplacement = ({
  modalVersion = false,
  defaultReplacement,
  foundContent,
  notFoundContent,
  onFound,
  isDisabled = false,
}: ContractReplacementProps) => {
  const { app, product } = useApp()
  const { getBaseUrl } = usePartner()
  const { insuranceParams } = useInsuranceParams()
  const [replacement, setReplacement] = useState(
    defaultReplacement ??
    !!insuranceParams?.generalParams.contractReplacementNumber,
  )
  const navigate = useNavigate()
  const { getFieldState, register, watch, getValues, setValue } =
    useFormContext<IInsuranceParams>()

  const [showResult, setShowResult] = useState(false)

  const contractNumber = watch('generalParams.contractReplacementNumber')
  const insurer = watch('generalParams.contractReplacementInsurerId')
  const { refetch, isLoading, isError, isFetched, data } =
    useFindContractNumber(contractNumber ?? '', insurer ?? 0, {
      enabled: false,
    })

  useEffect(() => {
    if (!isFetched) onFound?.(undefined)
  }, [isFetched])

  const foundParamsId = data?.data.insuranceParamsId

  const errors = getFieldState('generalParams.contractReplacementNumber').error
  const insurerErrors = getFieldState(
    'generalParams.contractReplacementInsurerId',
  ).error

  const { mutateAsync: cloneInsuranceParams, isPending: isLoadingClone } =
    useCloneInsuranceParams()
  const cloneReturnUrl = `${getBaseUrl()}/${GetAppPath(app)}steps/1`

  const onCloneClick = (paramsId: number) => {
    cloneInsuranceParams({ paramsId: paramsId.toString(), replacement: true })
      .then(({ data: { insuranceParamsId } }) => {
        navigate(`${cloneReturnUrl}/${insuranceParamsId}`)
      })
      .catch((data) => {
        alert('Při klonování došlo k chybě')
      })
  }

  const tooltipText = useMemo(() => getTooltipText(product!), [product])

  return (
    <>
      {!modalVersion && (
        <FormControl>
          <FormControl>
            <FormLabel tooltip={tooltipText}>Náhrada smlouvy</FormLabel>
          </FormControl>

          <FormControl
            sx={{
              display: 'flex',
              alignItems: 'center',
              alignSelf: 'end',
              py: 1,
            }}
          >
            <FormLabel htmlFor="contractReplacementSwitch" variant="switch">
              Ne
            </FormLabel>
            <Switch
              id="contractReplacementSwitch"
              isChecked={replacement}
              isDisabled={isDisabled}
              onChange={(e) => {
                if (!e.target.checked) {
                  setValue('generalParams.contractReplacementNumber', undefined)
                  setValue(
                    'generalParams.contractReplacementInsurerId',
                    undefined,
                  )
                }

                setReplacement(e.target.checked)
              }}
            />
            <FormLabel htmlFor="contractReplacementSwitch" variant="switch">
              Ano
            </FormLabel>
          </FormControl>
        </FormControl>
      )}

      {replacement && (
        <VStack gap={6}>
          <Grid w="100%" templateColumns={'1fr 1fr 1fr'} gap={4}>
            <FormControl isInvalid={!!errors}>
              <FormLabel htmlFor="contractReplacementNumber">
                Číslo smlouvy
              </FormLabel>

              <InputGroup
                id="contractReplacementNumber"
                placeholder="Číslo smlouvy"
                isDisabled={isDisabled}
                htmlSize={16}
                errors={errors}
                {...register('generalParams.contractReplacementNumber', {
                  required: 'Zadejte název nabídky',
                })}
              />
            </FormControl>

            <FormControl isInvalid={!!insurerErrors}>
              <FormLabel>Pojišťovna</FormLabel>
              <Select
                id="generalParams.contractReplacementInsurerId"
                isDisabled={isDisabled}
                value={insurer?.toString() ?? ''}
                onChange={(e) => {
                  setValue(
                    'generalParams.contractReplacementInsurerId',
                    parseInt(e.target.value),
                  )
                }}
              >
                <option value="" disabled>
                  Vyberte
                </option>
                <option value={Insurer.CPP.toString()}>ČPP</option>
                <option value={Insurer.Allianz.toString()}>Allianz</option>
                {app != Apps.property && (
                  <option value={Insurer.Kooperativa.toString()}>
                    Kooperativa
                  </option>
                )}
                {app == Apps.property && (
                  <option value={Insurer.Csob.toString()}>
                    ČSOB Pojišťovna
                  </option>
                )}
              </Select>
            </FormControl>

            {!isDisabled && (
              <Box
                sx={{
                  display: 'grid',
                  h: '100%',
                  alignItems: 'start',
                  pt: 9,
                }}
              >
                <Button
                  isLoading={isLoading}
                  isDisabled={
                    isLoading || !contractNumber || !insurer || !!errors
                  }
                  onClick={() => {
                    refetch()
                      .then(
                        ({ data }) =>
                          onFound?.(data?.data.insuranceParamsId ?? null),
                      )
                      .finally(() => {
                        setShowResult(true)
                      })
                  }}
                >
                  <Cloud color="secondary.500" />
                  Načíst údaje ze smlouvy
                </Button>
              </Box>
            )}
          </Grid>

          {!isLoading && showResult && (
            <>
              {!!foundParamsId &&
                (foundContent ? (
                  foundContent
                ) : (
                  <>
                    <Box>
                      Smlouva byla nalezena. Přejete si vyplnit data podle
                      původní smlouvy?
                    </Box>

                    <Button
                      isDisabled={isLoadingClone || app == Apps.property}
                      isLoading={isLoadingClone}
                      onClick={() => {
                        onCloneClick(foundParamsId)
                      }}
                    >
                      Vyplnit data
                    </Button>
                  </>
                ))}

              {(!foundParamsId || isError) &&
                isFetched &&
                (notFoundContent ? (
                  notFoundContent
                ) : (
                  <Box>
                    Smlouva nebyla nalezena. Načíst data z původní smlouvy není
                    možné, zadejte údaje ručně.
                  </Box>
                ))}
            </>
          )}
        </VStack>
      )}
    </>
  )
}
